import React, { useRef, useState } from 'react';
import { motion, useInView } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import { useLanguage } from '@/contexts/LanguageContext';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Maximize2, Layers, ChevronRight, ImageIcon } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const floors = ['All', '1st Floor', '2nd Floor', '3rd Floor', 'Ground Floor'];

export default function ListingsSection({ spaces }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-80px' });
  const { t, isRTL } = useLanguage();
  const [enquiryOpen, setEnquiryOpen] = useState(false);
  const [selectedSpace, setSelectedSpace] = useState(null);
  const [form, setForm] = useState({ name: '', phone: '', message: '' });
  const [sending, setSending] = useState(false);

  const openEnquiry = (space) => {
    setSelectedSpace(space);
    setForm({ name: '', phone: '', message: '' });
    setEnquiryOpen(true);
  };

  const handleEnquiry = async () => {
    if (!form.name || !form.phone) { toast.error('Please fill name and phone'); return; }
    setSending(true);
    try {
      await axios.post(`${API}/api/enquiries`, { space_id: selectedSpace?.id, space_name: selectedSpace?.name, name: form.name, phone: form.phone, message: form.message });
      toast.success('Enquiry submitted successfully!');
      setEnquiryOpen(false);
    } catch { toast.error('Failed to submit enquiry'); }
    finally { setSending(false); }
  };

  return (
    <section id="spaces" data-testid="listings-section" className="py-24 md:py-32 px-6 md:px-12 lg:px-24 bg-[#0B1120]">
      <div className="max-w-7xl mx-auto" ref={ref}>
        <motion.div initial={{ opacity: 0, y: 20 }} animate={inView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.6 }} className="text-center mb-12">
          <p className="text-xs tracking-[0.2em] uppercase text-[#D4AF37] font-bold mb-4 font-['Outfit']">{t('listings.label')}</p>
          <h2 className="text-3xl sm:text-4xl font-light text-[#F8FAFC] font-['Cormorant_Garamond'] tracking-tight">{t('listings.title')}</h2>
        </motion.div>

        <Tabs defaultValue="All" className="w-full">
          <div className="flex justify-center mb-10">
            <TabsList className="bg-[#0F172A] border border-white/10 p-1 h-auto flex-wrap">
              {floors.map(floor => (
                <TabsTrigger key={floor} value={floor} data-testid={`floor-tab-${floor.replace(/\s/g, '-').toLowerCase()}`} className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-[#020617] text-[#94A3B8] font-['Outfit'] text-sm px-4 py-2">
                  {floor === 'All' ? t('listings.all') : floor}
                </TabsTrigger>
              ))}
            </TabsList>
          </div>

          {floors.map(floor => (
            <TabsContent key={floor} value={floor}>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                {(floor === 'All' ? spaces : spaces.filter(s => s.floor === floor)).map((space, i) => (
                  <motion.div key={space.id || i} initial={{ opacity: 0, y: 20 }} animate={inView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.4, delay: i * 0.05 }} data-testid={`space-card-${space.id || i}`} className="group bg-[#0F172A] border border-white/5 rounded-lg overflow-hidden hover:border-[#D4AF37]/40 transition-all duration-300">
                    {/* Space Image */}
                    {space.images && space.images.length > 0 ? (
                      <div className="w-full h-40 overflow-hidden">
                        <img src={`${API}${space.images[0]}`} alt={space.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                      </div>
                    ) : (
                      <div className="w-full h-32 bg-[#1E293B] flex items-center justify-center">
                        <ImageIcon className="w-8 h-8 text-[#475569]" />
                      </div>
                    )}
                    <div className="p-5">
                      <div className="flex items-start justify-between mb-3">
                        <div>
                          <h3 className="text-lg font-medium text-[#F8FAFC] font-['Cormorant_Garamond']">{space.name}</h3>
                          <p className="text-xs text-[#94A3B8] font-['Outfit'] mt-1">{space.floor}</p>
                        </div>
                        <Badge className={`text-[10px] ${space.status === 'Available' ? 'bg-[#D4AF37]/15 text-[#D4AF37] border border-[#D4AF37]/30' : space.status === 'Reserved' ? 'bg-blue-500/15 text-blue-400 border border-blue-500/30' : 'bg-[#94A3B8]/15 text-[#94A3B8] border border-[#94A3B8]/30'}`}>
                          {space.status}
                        </Badge>
                      </div>
                      <div className="flex items-center gap-4 mb-3 text-[#94A3B8]">
                        <div className="flex items-center gap-1.5 text-xs font-['Outfit']"><Maximize2 className="w-3.5 h-3.5" />{space.size}</div>
                        <div className="flex items-center gap-1.5 text-xs font-['Outfit']"><Layers className="w-3.5 h-3.5" />{space.space_type}</div>
                      </div>
                      <p className="text-xs text-[#64748B] font-['Outfit'] mb-4 line-clamp-2">{space.description}</p>
                      {space.status === 'Available' && (
                        <button data-testid={`enquire-btn-${space.id || i}`} onClick={() => openEnquiry(space)} className="w-full flex items-center justify-center gap-2 text-sm font-medium text-[#D4AF37] border border-[#D4AF37]/30 rounded-sm py-2.5 hover:bg-[#D4AF37]/10 transition-all duration-300 font-['Outfit']">
                          {t('listings.enquireNow')} <ChevronRight className={`w-3.5 h-3.5 ${isRTL ? 'rotate-180' : ''}`} />
                        </button>
                      )}
                    </div>
                  </motion.div>
                ))}
              </div>
              {(floor === 'All' ? spaces : spaces.filter(s => s.floor === floor)).length === 0 && (
                <p className="text-center text-[#94A3B8] py-12 font-['Outfit']">{t('listings.noSpaces')}</p>
              )}
            </TabsContent>
          ))}
        </Tabs>
      </div>

      <Dialog open={enquiryOpen} onOpenChange={setEnquiryOpen}>
        <DialogContent className="bg-[#0F172A] border-white/10 text-[#F8FAFC] max-w-md">
          <DialogHeader>
            <DialogTitle className="font-['Cormorant_Garamond'] text-2xl font-light">{t('listings.enquireTitle')} {selectedSpace?.name}</DialogTitle>
            <DialogDescription className="text-[#94A3B8] font-['Outfit']">{selectedSpace?.floor} &middot; {selectedSpace?.size} &middot; {selectedSpace?.space_type}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-[#94A3B8] text-xs uppercase tracking-wider font-bold">{t('listings.yourName')}</Label>
              <Input data-testid="enquiry-name-input" value={form.name} onChange={e => setForm({...form, name: e.target.value})} placeholder={t('listings.yourName')} className="bg-[#1E293B] border-white/10 text-[#F8FAFC] mt-1 placeholder:text-[#475569]" />
            </div>
            <div>
              <Label className="text-[#94A3B8] text-xs uppercase tracking-wider font-bold">{t('listings.phone')}</Label>
              <Input data-testid="enquiry-phone-input" value={form.phone} onChange={e => setForm({...form, phone: e.target.value})} placeholder="+91 XXXXXXXXXX" className="bg-[#1E293B] border-white/10 text-[#F8FAFC] mt-1 placeholder:text-[#475569]" />
            </div>
            <div>
              <Label className="text-[#94A3B8] text-xs uppercase tracking-wider font-bold">{t('listings.message')}</Label>
              <Textarea data-testid="enquiry-message-input" value={form.message} onChange={e => setForm({...form, message: e.target.value})} className="bg-[#1E293B] border-white/10 text-[#F8FAFC] mt-1 placeholder:text-[#475569]" rows={3} />
            </div>
          </div>
          <DialogFooter>
            <Button data-testid="enquiry-submit-btn" onClick={handleEnquiry} disabled={sending} className="bg-[#D4AF37] text-[#020617] hover:bg-[#FCE69C] font-medium">
              {sending ? t('listings.submitting') : t('listings.submit')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </section>
  );
}
